/**
 * Zod API contracts for auth routes (registration, login, refresh, security).
 *
 * Single source of truth for request body validation.
 * Use `z.infer<typeof Schema>` to derive TypeScript types.
 */

import { z } from 'zod';

// =============================================================================
// Auth Core Contracts
// =============================================================================
/**
 * What a display-name input accepts, shared so web and mobile cap at the same point.
 * Deliberately below the 50 that validateRequiredDisplayName tolerates: the longest name
 * in production is 15 characters, so 50 is room nobody uses.
 */
export const DISPLAY_NAME_INPUT_MAX_LENGTH = 30;

export const RegisterBodySchema = z.object({
  email: z
    .string()
    .email('Invalid email format')
    .max(255, 'Email is too long')
    .transform((v) => v.trim().toLowerCase()),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password is too long'),
  firstName: z.string().min(1, 'First name is required').max(30, 'First name is too long'),
  lastName: z.string().min(1, 'Last name is required').max(30, 'Last name is too long'),
  displayName: z
    .string()
    .min(2, 'Display name must be at least 2 characters')
    .max(50, 'Display name must be 50 characters or less'),
  tosAccepted: z.boolean().optional(),
  inviteCode: z.string().optional(),
});

export type RegisterBody = z.infer<typeof RegisterBodySchema>;

export const LoginBodySchema = z.object({
  email: z
    .string()
    .email('Invalid email format')
    .max(255, 'Email is too long')
    .transform((v) => v.trim().toLowerCase()),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});

export type LoginBody = z.infer<typeof LoginBodySchema>;

export const RefreshBodySchema = z.object({
  refreshToken: z.string().optional(),
});

export type RefreshBody = z.infer<typeof RefreshBodySchema>;

// =============================================================================
// Auth Security Contracts
// =============================================================================

export const ForgotPasswordBodySchema = z.object({
  email: z
    .string()
    .email('Invalid email format')
    .max(255, 'Email is too long')
    .transform((v) => v.trim().toLowerCase()),
});

export type ForgotPasswordBody = z.infer<typeof ForgotPasswordBodySchema>;

export const ResetPasswordBodySchema = z.object({
  token: z.string().min(1, 'Reset token is required'),
  newPassword: z
    .string()
    .min(8, 'New password must be at least 8 characters')
    .max(128, 'Password is too long'),
});

export type ResetPasswordBody = z.infer<typeof ResetPasswordBodySchema>;

export const VerifyEmailBodySchema = z.object({
  token: z.string().min(1, 'Verification token is required'),
});

export type VerifyEmailBody = z.infer<typeof VerifyEmailBodySchema>;

export const ResendVerificationPublicBodySchema = z.object({
  email: z
    .string()
    .email('Invalid email format')
    .max(255, 'Email is too long')
    .transform((v) => v.trim().toLowerCase()),
});

export type ResendVerificationPublicBody = z.infer<typeof ResendVerificationPublicBodySchema>;

export const EmailChangeBodySchema = z.object({
  newEmail: z
    .string()
    .email('Invalid email format')
    .max(255, 'Email is too long')
    .transform((v) => v.trim().toLowerCase()),
  password: z.string().min(1, 'Password is required'),
});

export type EmailChangeBody = z.infer<typeof EmailChangeBodySchema>;

export const ChangePasswordBodySchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z
    .string()
    .min(8, 'New password must be at least 8 characters')
    .max(128, 'Password is too long'),
});

export type ChangePasswordBody = z.infer<typeof ChangePasswordBodySchema>;

export const LogoutAllBodySchema = z.object({
  keepCurrent: z.boolean().optional(),
});

export type LogoutAllBody = z.infer<typeof LogoutAllBodySchema>;

export const DeleteAccountBodySchema = z.object({
  password: z.string().optional(),
  verificationType: z.enum(['password', 'oauth']).optional(),
  // OAuth step-up re-auth (passwordless accounts): verified in the handler.
  provider: z.enum(['google', 'apple']).optional(),
  idToken: z.string().optional(),
});

export type DeleteAccountBody = z.infer<typeof DeleteAccountBodySchema>;

export const PauseAccountBodySchema = z.object({
  password: z.string().optional(),
  verificationType: z.enum(['password', 'oauth']).optional(),
  // OAuth step-up re-auth (passwordless accounts): verified in the handler.
  provider: z.enum(['google', 'apple']).optional(),
  idToken: z.string().optional(),
});

export type PauseAccountBody = z.infer<typeof PauseAccountBodySchema>;

export const RecoverAccountBodySchema = z.object({
  email: z
    .string()
    .email('Invalid email format')
    .max(255, 'Email is too long')
    .transform((v) => v.trim().toLowerCase()),
  password: z.string().optional(),
  provider: z.enum(['google', 'apple']).optional(),
  idToken: z.string().optional(),
});

export type RecoverAccountBody = z.infer<typeof RecoverAccountBodySchema>;

export const ResumeAccountBodySchema = z.object({
  email: z
    .string()
    .email('Invalid email format')
    .max(255, 'Email is too long')
    .transform((v) => v.trim().toLowerCase()),
  password: z.string().optional(),
  provider: z.enum(['google', 'apple']).optional(),
  idToken: z.string().optional(),
});

export type ResumeAccountBody = z.infer<typeof ResumeAccountBodySchema>;
